import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useMatch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { selectIsOnboardingInProgress } from '@/store/slices/session';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { usePointerGlow } from '../../hooks/usePointerGlow';
import { useResumePaidSession } from '../../hooks/useResumePaidSession';
import { CFG_MOBILE } from '../../theme/configurator-tokens';
import { attachConfiguratorClickTracking } from './lib/analytics';
import { useStartProperties } from './lib/useStartProperties';
import { usePreloadImage } from './lib/preloadImage';
import { findThemeScreenshot } from './data/preview';
import { CONFIGURATOR_ROUTE } from './routes';
import { ConfiguratorProvider, useConfigurator } from './store/configurator';
import { MobileApp } from './mobile/MobileApp';
import { CountrySelector } from './country/CountrySelector';
import { UserMenu } from './components/wizard';
import { PreviewStage, PreviewStageProvider } from './components/preview-stage';
import { PreviewOverlay } from './steps/finish/components/PreviewOverlay';
import { Root, ErrorScreen, ErrorTitle, ErrorBody } from './configurator-v0.styles';

/**
 * Renders the active step (desktop) or the mobile experience, plus the country
 * picker overlay. Must live inside `ConfiguratorProvider` (reads its status).
 */
const ConfiguratorInner = (): React.ReactElement => {
  const isMobile = useMediaQuery(CFG_MOBILE);
  const { i18n } = useTranslation();
  const { status, selectedTheme, selectedColor, selectedStyle } = useConfigurator();

  // Warm the Finish-step screenshots as soon as the design selection resolves (step
  // 1 onward), not when DevicePreview mounts on step 4 — so by the time the wizard
  // gets there the image is already cached and paints instantly instead of showing
  // its loading placeholder.
  const lang = (i18n.language || 'en').split('-')[0];
  const finishScreenshot = findThemeScreenshot(selectedTheme, selectedColor, selectedStyle, lang);
  usePreloadImage(finishScreenshot?.desktop ?? '');
  usePreloadImage(finishScreenshot?.iphone ?? '');

  if (isMobile) return <MobileApp />;

  if (status === 'error') {
    return (
      <ErrorScreen>
        <ErrorTitle text="Something went wrong" tag="h1" />
        <ErrorBody text="We couldn't load the configurator. Please refresh and try again." tag="paragraph" />
      </ErrorScreen>
    );
  }

  return (
    <>
      <Outlet />
      <CountrySelector />
      {/* Account icon + popover — desktop only (mobile placement is a later task). */}
      <UserMenu />
      {/* Full-screen "Live Preview" overlay, mounted ONCE at provider scope so every step
          can open it (fullscreenOpen is provider-scoped), including the build steps, whose
          tablet static previews now surface a "Live Preview" button. Renders null until
          opened. */}
      <PreviewOverlay />
    </>
  );
};

/**
 * configurator-v0 area root — the full-bleed wizard's layout route. Wraps every
 * step in the shared `ConfiguratorProvider` (so wizard state persists across step
 * navigation) and the full-bleed page shell.
 *
 * The wizard's brand font (Satoshi, `CFG_FONT`) is loaded in `index.html` — the
 * owners-hub shell doesn't ship it otherwise and the headlines fall back to the
 * system font.
 *
 * Sets the browser-tab title for the whole wizard. Unlike the account-area pages
 * (see `nova/router.tsx` → `DocumentTitle`, which prefixes "Owner Studio
 * - "), this is a standalone full-bleed experience with its own title format. The
 * page name goes through `t()` (English-as-key) to stay translatable; the brand
 * suffix is a literal per the request. No chrome competes for the title here —
 * `FixedFullBleedPage` is auth-gate only — so this single Helmet owns the tab.
 */
export const ConfiguratorRoot = (): React.ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const atBase = useMatch(CONFIGURATOR_ROUTE.base());
  // You can only build one website at a time. If a PAID brand is still being set up
  // (not yet live), the configurator's BUILD steps are off-limits: bounce to Home, where
  // the "Finish setting up…" Spotlight banner resumes onboarding. Covers a warm visit
  // (status already loaded) and a cold one (the ConfiguratorProvider resumes the paid
  // session below, its status flips, and this redirect then fires).
  //
  // The PAYMENT step is EXEMPT: that's where a just-completed checkout lands (the in-page
  // success swap, or a Stripe 3DS redirect return to this path), and the step renders its
  // own "Payment successful" screen — bouncing it would swallow that confirmation (which
  // is exactly the regression this guards against). An UNPAID build (`configuration`) is
  // never caught here either (its status isn't in the set), so it resumes as before.
  // A session can come back from `/session/load` with an invoice that is ALREADY PAID while
  // the session itself is still `configuration` — the backend waits to be told before it
  // advances one. This reads the invoice once and, when that is the case, acknowledges the
  // payment (exactly once per session) so the build can proceed instead of dead-ending.
  const { showConfirmation } = useResumePaidSession();

  const onboardingInProgress = useAppSelector(selectIsOnboardingInProgress);
  const onFinishStep = location.pathname.endsWith('/payment');

  // Consume the `?startProperties=<base64-JSON>` hand-off from the external marketing site:
  // apply the owner's `locale` (UI language + saved to user-preferences) and persist their
  // tracking `consent` for the tracking scripts wired up later. Once, on entry — kept above
  // the redirect guard below so all hooks run before any conditional return.
  useStartProperties();

  // Drive the cursor-following card/tile hover glow (the studio's `hoverWash`, now the
  // wizard's card hover). The controller lives in MainLayout for the rest of the studio,
  // but this full-bleed wizard isn't inside it, so mount it here; without it the glow
  // still shows, just centred instead of tracking the pointer.
  usePointerGlow();

  // Auto-capture analytics for EVERY button click in the configurator (incl. its
  // portalled modals) — so any button anyone adds later is tracked without extra wiring.
  // Explicit `data-ga-id="cfg-<page>-<action>"` tags just sharpen the reporting.
  useEffect(() => attachConfiguratorClickTracking(), []);

  // A session that arrives ALREADY PAID has just been acknowledged by the hook above, which
  // advances it to `customization` — putting it in ONBOARDING_STATUSES and tripping the guard
  // below. That would bounce the buyer to Studio Home before they ever see the confirmation
  // they paid for, so send them to the checkout step (which owns the success screen) FIRST.
  if (showConfirmation && !onFinishStep) {
    return <Navigate to={CONFIGURATOR_ROUTE.payment()} replace />;
  }

  if (onboardingInProgress && !onFinishStep) {
    return <Navigate to={OWNERS_HUB_ROUTES.STUDIO_HOME} replace />;
  }

  // Mobile does not render the Outlet containing the index-route redirect.
  if (atBase) {
    return (
      <Navigate
        to={{ pathname: CONFIGURATOR_ROUTE.theme(), search: location.search, hash: location.hash }}
        state={location.state}
        replace
      />
    );
  }

  return (
    <Root>
      <Helmet>
        <title>{`${t('Configurator')} - Whitelabels.com`}</title>
      </Helmet>
      <ConfiguratorProvider>
        <PreviewStageProvider>
          {/* Persistent preview host: owns the two warm preview iframes for the whole
              wizard. Rendered BEFORE the steps so it paints beneath the (transparent)
              step panes — the device shows through, the chrome paints on top — and it
              survives every step navigation, so re-showing a preview never reloads. */}
          <PreviewStage />
          <ConfiguratorInner />
        </PreviewStageProvider>
      </ConfiguratorProvider>
    </Root>
  );
};

export default ConfiguratorRoot;
